import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Select } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { addCharge, updateCharge } from "@/services/chargeService";
import { annualOf, CHARGE_ICONS } from "@/utils/charges";
import { formatMoney } from "@/utils/format";
import type { Charge, ChargeCategory, Cycle } from "@/types";
import { currencySymbol } from "@/utils/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: Charge | null;
  categories: ChargeCategory[];
  cycles: Cycle[];
}

interface CycleRow {
  checked: boolean;
  monthly: string;
}

export default function ChargeFormModal({ open, onClose, onSaved, editing, categories, cycles }: Props) {
  const { notify } = useToast();
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(CHARGE_ICONS[0]);
  const [categoryId, setCategoryId] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [oncePerMonth, setOncePerMonth] = useState(false);
  const [rows, setRows] = useState<Record<string, CycleRow>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setIcon(editing.icon);
      setCategoryId(editing.categoryId);
      setIsActive(editing.isActive);
      setOncePerMonth(editing.oncePerMonth);
      const next: Record<string, CycleRow> = {};
      cycles.forEach((c) => {
        const amount = editing.amounts.find((a) => a.cycleId === c.id);
        next[c.id] = { checked: !!amount && amount.monthly > 0, monthly: amount ? String(amount.monthly) : "" };
      });
      setRows(next);
    } else {
      setName("");
      setIcon(CHARGE_ICONS[0]);
      setCategoryId(categories[0]?.id ?? "");
      setIsActive(true);
      setOncePerMonth(false);
      const next: Record<string, CycleRow> = {};
      cycles.forEach((c) => {
        next[c.id] = { checked: false, monthly: "" };
      });
      setRows(next);
    }
  }, [open, editing, categories, cycles]);

  const setRow = (cycleId: string, patch: Partial<CycleRow>) =>
    setRows((prev) => ({ ...prev, [cycleId]: { ...prev[cycleId], ...patch } }));

  const handleSave = async () => {
    if (!name.trim()) {
      notify("Le nom de la charge est obligatoire.", "error");
      return;
    }
    if (!categoryId) {
      notify("Veuillez choisir une catégorie.", "error");
      return;
    }
    const amounts = cycles
      .filter((c) => rows[c.id]?.checked && Number(rows[c.id]?.monthly) > 0)
      .map((c) => ({ cycleId: c.id, monthly: Number(rows[c.id].monthly) }));

    if (amounts.length === 0) {
      notify("Cochez au moins un cycle et saisissez un montant prévisionnel mensuel.", "error");
      return;
    }

    setBusy(true);
    const payload = { name: name.trim(), categoryId, icon, amounts, isActive, oncePerMonth };
    try {
      if (editing) {
        await updateCharge(editing.id, payload);
        notify("Charge mise à jour.");
      } else {
        await addCharge(payload);
        notify(`Charge « ${name.trim()} » ajoutée.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Modifier la charge" : "Nouvelle charge"}
      subtitle="Rattachez la charge à une catégorie et définissez son montant prévisionnel"
      icon="ri-stack-line"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={busy}>
            {busy ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Nom de la charge"
            required
            placeholder="Ex : Loyer, EDG, SEG..."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Select label="Catégorie" required value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">— Choisir —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-foreground-700">Icône</p>
          <div className="flex flex-wrap gap-2">
            {CHARGE_ICONS.map((ic) => (
              <button
                key={ic}
                type="button"
                onClick={() => setIcon(ic)}
                className={`w-9 h-9 flex items-center justify-center rounded-md border transition-colors cursor-pointer ${
                  icon === ic
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-300 text-foreground-500 hover:bg-background-100"
                }`}
                aria-label={ic}
              >
                <i className={`${ic} text-lg`}></i>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-foreground-700">
            Montants prévisionnels mensuels <span className="text-accent-600">*</span>
          </p>
          <div className="space-y-3">
            {cycles.map((c) => {
              const row = rows[c.id] ?? { checked: false, monthly: "" };
              return (
                <div key={c.id} className="rounded-md border border-background-200 bg-background-100/50 p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={row.checked}
                      onChange={(e) => setRow(c.id, { checked: e.target.checked })}
                      className="h-4 w-4 shrink-0 cursor-pointer accent-primary-500"
                    />
                    <span className="text-sm font-medium text-foreground-800">{c.label}</span>
                  </label>
                  {row.checked && (
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Input
                        label={`Mensuel (${currencySymbol()})`}
                        type="number"
                        placeholder="Ex : 1200000"
                        value={row.monthly}
                        onChange={(e) => setRow(c.id, { monthly: e.target.value })}
                      />
                      <div className="flex flex-col justify-end">
                        <span className="mb-1.5 block text-xs font-medium text-foreground-700">Annuel (auto)</span>
                        <span className="rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-800">
                          {formatMoney(annualOf(Number(row.monthly) || 0))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-primary-500"
          />
          <span className="text-sm text-foreground-800">Charge active (proposée à la saisie des dépenses)</span>
        </label>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={oncePerMonth}
            onChange={(e) => setOncePerMonth(e.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer accent-primary-500"
          />
          <span className="text-sm text-foreground-800">
            Une seule dépense par mois et par cycle
            <span className="block text-xs text-foreground-500">
              Ex. loyer, électricité : empêche de payer deux fois la même charge dans le même mois.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}